// ============================================================
// colorUtils.ts
// WCAG-contrast helpers for tag text colour selection.
// Dependency-free. Safe for SSR (graceful fallback when no DOM).
// ============================================================

/**
 * Resolve a CSS variable reference to a concrete colour string.
 * e.g. "var(--mg-primary)" → "#1677ff"
 *
 * Returns null when:
 *  - the DOM is not available (SSR)
 *  - the property is not set / empty
 *  - the value does not look like a colour
 */
export function resolveCSSVar(value: string): string | null {
  if (typeof document === 'undefined') return null;

  // Strip "var(" prefix and ")" suffix, and any fallback after a comma.
  const match = /^var\(\s*(--[^,)]+)/.exec(value.trim());
  if (!match) return null;

  const propName = match[1].trim();
  const resolved = getComputedStyle(document.documentElement)
    .getPropertyValue(propName)
    .trim();

  return resolved || null;
}

/**
 * Parse a colour string into [r, g, b] in 0–255 range.
 * Supports:
 *   - #rgb  (3-digit hex)
 *   - #rrggbb  (6-digit hex)
 *   - rgb(r, g, b)
 *   - rgba(r, g, b, a)
 *
 * Returns null for unrecognised formats.
 */
function parseRGB(color: string): [number, number, number] | null {
  const s = color.trim();

  // #rgb → expand to #rrggbb
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(s);
  if (short) {
    return [
      parseInt(short[1] + short[1], 16),
      parseInt(short[2] + short[2], 16),
      parseInt(short[3] + short[3], 16),
    ];
  }

  // #rrggbb or #rrggbbaa (alpha ignored for luminance)
  const full = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(s);
  if (full) {
    return [
      parseInt(full[1], 16),
      parseInt(full[2], 16),
      parseInt(full[3], 16),
    ];
  }

  // rgb(...) / rgba(...)
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/.exec(s);
  if (rgb) {
    return [parseInt(rgb[1], 10), parseInt(rgb[2], 10), parseInt(rgb[3], 10)];
  }

  return null;
}

/**
 * Compute the WCAG relative luminance of an sRGB triplet.
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
function relativeLuminance(r: number, g: number, b: number): number {
  const toLinear = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Given a background colour string, return '#000000' or '#ffffff' for
 * maximum readability based on WCAG relative luminance contrast.
 *
 * Accepts:
 *   - hex strings: '#rgb', '#rrggbb'
 *   - rgb/rgba strings: 'rgb(r, g, b)', 'rgba(r, g, b, a)'
 *   - CSS variable references: 'var(--some-prop)'
 *
 * Falls back to '#000000' (dark text) when the colour cannot be resolved
 * (e.g. SSR, unset CSS var, unrecognised format).
 */
export function getReadableTextColor(background: string): '#000000' | '#ffffff' {
  let resolved = background.trim();

  // Resolve CSS variable to a concrete value first
  if (resolved.startsWith('var(')) {
    const varValue = resolveCSSVar(resolved);
    if (!varValue) return '#000000'; // fallback: dark text
    resolved = varValue.trim();
  }

  const rgb = parseRGB(resolved);
  if (!rgb) return '#000000'; // unrecognised format → safe fallback

  const lum = relativeLuminance(rgb[0], rgb[1], rgb[2]);

  // WCAG contrast ratio: (L1 + 0.05) / (L2 + 0.05)
  // White (L=1) vs background: (1.05) / (lum + 0.05)
  // Black (L=0) vs background: (lum + 0.05) / (0.05)
  // Choose whichever gives higher contrast.
  const contrastWithWhite = 1.05 / (lum + 0.05);
  const contrastWithBlack = (lum + 0.05) / 0.05;

  return contrastWithWhite >= contrastWithBlack ? '#ffffff' : '#000000';
}
